import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Mail,
  CheckCircle,
  Eye,
  MousePointer,
  Play,
  Pause,
  Edit,
  Users,
  BarChart3,
  Trash2,
  UserPlus,
} from 'lucide-react'
import { useCampaign, useStartCampaign, usePauseCampaign, useResumeCampaign } from '@hooks/useCampaigns'
import { campaignService } from '@services/campaign.service'
import { formatDateTime, formatNumber, formatPercentage } from '@lib/utils'
import { EditCampaignDialog } from './EditCampaignDialog'

type TabType = 'overview' | 'clients'

interface CampaignClient {
  id: number
  email: string
  fullName: string | null
  company: string | null
  status: string
  sequenceStatus: string | null
  currentStep: number | null
  nextSendAt: string | null
}

export function CampaignDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaignId = Number(id)
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [showEditDialog, setShowEditDialog] = useState(false)

  const { data: campaign, isLoading, error, refetch } = useCampaign(campaignId)
  const startMutation = useStartCampaign()
  const pauseMutation = usePauseCampaign()
  const resumeMutation = useResumeCampaign()

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-600">Failed to load campaign details</p>
        <button
          onClick={() => navigate('/campaigns')}
          className="mt-4 text-sm text-red-700 underline"
        >
          Back to campaigns
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/campaigns')}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-slate-100">{campaign.name}</h1>
            <p className="text-sm text-slate-500">Campaign ID: {campaign.id}</p>
          </div>
        </div>

        <div className="flex gap-2">
          {campaign.status === 'DRAFT' && (
            <button
              onClick={() => startMutation.mutate(campaign.id)}
              disabled={startMutation.isPending}
              className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              <Play size={18} />
              Start Campaign
            </button>
          )}

          {campaign.status === 'ACTIVE' && (
            <button
              onClick={() => pauseMutation.mutate(campaign.id)}
              disabled={pauseMutation.isPending}
              className="flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
            >
              <Pause size={18} />
              Pause
            </button>
          )}

          {campaign.status === 'PAUSED' && (
            <button
              onClick={() => resumeMutation.mutate(campaign.id)}
              disabled={resumeMutation.isPending}
              className="flex items-center gap-2 rounded-xl bg-cyan-600 px-4 py-2 text-white hover:bg-cyan-700 disabled:opacity-50 transition-colors"
            >
              <Play size={18} />
              Resume
            </button>
          )}

          <button
            onClick={() => setShowEditDialog(true)}
            className="flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-slate-200 hover:bg-slate-700 transition-colors"
          >
            <Edit size={18} />
            Edit
          </button>
        </div>
      </div>

      {/* Edit Campaign Dialog */}
      <EditCampaignDialog
        isOpen={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        campaign={campaign}
        onSuccess={() => refetch()}
      />

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'overview'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-400 hover:border-slate-600 hover:text-slate-300'
            }`}
          >
            <BarChart3 size={18} />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'clients'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-400 hover:border-slate-600 hover:text-slate-300'
            }`}
          >
            <Users size={18} />
            Clients
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab campaign={campaign} />}
      {activeTab === 'clients' && <ClientsTab campaignId={campaignId} campaign={campaign} />}
    </div>
  )
}

// Overview Tab Component
function OverviewTab({ campaign }: { campaign: any }) {
  // Extract stats with defaults to handle undefined values
  const totalClients = campaign.totalClients ?? 0
  const emailsSent = campaign.emailsSent ?? 0
  const emailsDelivered = campaign.emailsDelivered ?? 0
  const opensCount = campaign.opensCount ?? 0
  const clicksCount = campaign.clicksCount ?? 0

  const deliveryRate = emailsSent > 0 ? emailsDelivered / emailsSent : 0
  const openRate = emailsDelivered > 0 ? opensCount / emailsDelivered : 0
  const clickRate = opensCount > 0 ? clicksCount / opensCount : 0
  const progress = totalClients > 0 ? (emailsSent / totalClients) * 100 : 0

  return (
    <div className="space-y-6">
      {/* Status & Progress */}
      <div className="stat-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-100">Campaign Progress</h2>
          <StatusBadge status={campaign.status} sendingStatus={campaign.sendingStatus} />
        </div>

        <div className="mb-2 flex justify-between text-sm text-slate-400">
          <span>Progress</span>
          <span>
            {formatNumber(emailsSent)} / {formatNumber(totalClients)} emails sent
          </span>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-slate-700">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-indigo-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="mt-2 text-sm text-slate-500">{progress.toFixed(1)}% complete</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Mail} label="Emails Sent" value={formatNumber(emailsSent)} color="blue" />
        <StatCard
          icon={CheckCircle}
          label="Delivered"
          value={formatNumber(emailsDelivered)}
          subtitle={formatPercentage(deliveryRate)}
          color="green"
        />
        <StatCard
          icon={Eye}
          label="Opens"
          value={formatNumber(opensCount)}
          subtitle={formatPercentage(openRate)}
          color="purple"
        />
        <StatCard
          icon={MousePointer}
          label="Clicks"
          value={formatNumber(clicksCount)}
          subtitle={formatPercentage(clickRate)}
          color="orange"
        />
      </div>

      {/* Campaign Info */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="stat-card p-6">
          <h3 className="mb-4 text-lg font-semibold text-slate-100">Campaign Details</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-slate-400">Status</dt>
              <dd className="text-sm text-slate-200">{campaign.status}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-slate-400">Sending Status</dt>
              <dd className="text-sm text-slate-200">{campaign.sendingStatus || 'idle'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-slate-400">Total Recipients</dt>
              <dd className="text-sm text-slate-200">{formatNumber(totalClients)}</dd>
            </div>
          </dl>
        </div>

        <div className="stat-card p-6">
          <h3 className="mb-4 text-lg font-semibold text-slate-100">Timeline</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-slate-400">Created</dt>
              <dd className="text-sm text-slate-200">{formatDateTime(campaign.createdAt)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-slate-400">Last Updated</dt>
              <dd className="text-sm text-slate-200">{formatDateTime(campaign.updatedAt)}</dd>
            </div>
            {campaign.completedAt && (
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-slate-400">Completed</dt>
                <dd className="text-sm text-slate-200">{formatDateTime(campaign.completedAt)}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="stat-card p-6">
        <h3 className="mb-4 text-lg font-semibold text-slate-100">Performance Metrics</h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <MetricCard label="Delivery Rate" value={formatPercentage(deliveryRate)} color="green" />
          <MetricCard label="Open Rate" value={formatPercentage(openRate)} color="blue" />
          <MetricCard label="Click-Through Rate" value={formatPercentage(clickRate)} color="purple" />
        </div>
      </div>
    </div>
  )
}

// Clients Tab Component
function ClientsTab({ campaignId, campaign }: { campaignId: number; campaign: any }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const limit = 20

  // Calculate total emails in campaign sequence
  const totalEmailsInCampaign = [
    campaign.template1Id,
    campaign.template2Id,
    campaign.template3Id,
    campaign.template4Id,
    campaign.template5Id,
  ].filter((id) => id !== null).length || 1

  const { data, isLoading, error } = useQuery({
    queryKey: ['campaign-clients', campaignId, page],
    queryFn: () => campaignService.getCampaignClients(campaignId, { page, limit }),
  })

  const removeClientMutation = useMutation({
    mutationFn: (clientId: number) => campaignService.removeClientFromCampaign(campaignId, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-clients', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })

  const handleRemoveClient = (clientId: number, email: string) => {
    if (window.confirm(`Are you sure you want to remove ${email} from this campaign?`)) {
      removeClientMutation.mutate(clientId)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-600">Failed to load campaign clients</p>
      </div>
    )
  }

  const clients: CampaignClient[] = data?.clients || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / (data?.limit || 20))

  return (
    <div className="space-y-4">
      {/* Header with Add Clients button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-100">Campaign Clients</h3>
          <p className="text-sm text-slate-500">
            {total} client{total !== 1 ? 's' : ''} assigned to this campaign
          </p>
        </div>
        <button
          onClick={() => navigate(`/clients?addToCampaign=${campaignId}`)}
          className="btn-primary"
        >
          <UserPlus size={18} />
          Add Clients
        </button>
      </div>

      {/* Clients Table */}
      {clients.length === 0 ? (
        <div className="stat-card p-12 text-center">
          <Users size={48} className="mx-auto text-slate-600" />
          <h3 className="mt-4 text-lg font-medium text-slate-200">No clients assigned</h3>
          <p className="mt-2 text-sm text-slate-500">
            Add clients to this campaign to start sending emails
          </p>
          <button
            onClick={() => navigate(`/clients?addToCampaign=${campaignId}`)}
            className="btn-primary mt-4"
          >
            <UserPlus size={18} />
            Add Clients
          </button>
        </div>
      ) : (
        <div className="stat-card overflow-hidden p-0">
          <table className="min-w-full divide-y divide-slate-700/50">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Sequence Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Next Send
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {clients.map((client) => (
                <tr key={client.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div>
                      <p className="font-medium text-slate-200">
                        {client.fullName || 'Unknown'}
                      </p>
                      <p className="text-sm text-slate-400">{client.email}</p>
                      {client.company && (
                        <p className="text-xs text-slate-500">{client.company}</p>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getSequenceStatusColor(client.sequenceStatus)}`}>
                      {client.sequenceStatus || 'Not Started'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <SequenceProgress
                      currentStep={client.currentStep ?? 0}
                      totalEmails={totalEmailsInCampaign}
                      sequenceStatus={client.sequenceStatus}
                    />
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-400">
                    <NextSendDisplay
                      nextSendAt={client.nextSendAt}
                      currentStep={client.currentStep ?? 0}
                      totalEmails={totalEmailsInCampaign}
                      sequenceStatus={client.sequenceStatus}
                    />
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right">
                    <button
                      onClick={() => handleRemoveClient(client.id, client.email)}
                      disabled={removeClientMutation.isPending}
                      className="text-rose-400 hover:text-rose-300 disabled:opacity-50 transition-colors"
                      title="Remove from campaign"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-700/50 px-6 py-3">
              <p className="text-sm text-slate-400">
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
                {total} clients
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function getSequenceStatusColor(status: string | null): string {
  switch (status) {
    case 'ACTIVE':
      return 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
    case 'PAUSED':
      return 'bg-amber-500/15 text-amber-400 border border-amber-500/20'
    case 'COMPLETED':
      return 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20'
    case 'CANCELLED':
      return 'bg-rose-500/15 text-rose-400 border border-rose-500/20'
    default:
      return 'bg-slate-500/15 text-slate-400 border border-slate-500/20'
  }
}

// Next Send Display Component - shows when next email will be sent (or completed)
function NextSendDisplay({
  nextSendAt,
  currentStep,
  totalEmails,
  sequenceStatus,
}: {
  nextSendAt: string | null
  currentStep: number
  totalEmails: number
  sequenceStatus: string | null
}) {
  // Calculate emails sent (currentStep - 1, capped at totalEmails)
  const emailsSent = Math.min(Math.max(currentStep - 1, 0), totalEmails)
  const isCompleted = sequenceStatus === 'COMPLETED' || emailsSent >= totalEmails

  if (currentStep === 0) {
    return <span className="text-slate-500">Not started</span>
  }

  if (isCompleted) {
    return <span className="text-emerald-400">All emails sent</span>
  }

  if (!nextSendAt) {
    return <span className="text-slate-500">Not scheduled</span>
  }

  return <span className="text-slate-300">{formatDateTime(nextSendAt)}</span>
}

// Sequence Progress Component - shows emails sent vs total in campaign
function SequenceProgress({
  currentStep,
  totalEmails,
  sequenceStatus,
}: {
  currentStep: number
  totalEmails: number
  sequenceStatus: string | null
}) {
  // currentStep represents the NEXT email to send (1-indexed)
  // So emails sent = currentStep - 1 (but min 0)
  // However if currentStep > totalEmails, all emails are sent
  const emailsSent = Math.min(Math.max(currentStep - 1, 0), totalEmails)
  const isCompleted = sequenceStatus === 'COMPLETED' || emailsSent >= totalEmails

  if (currentStep === 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-500">Not started</span>
      </div>
    )
  }

  if (isCompleted) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-emerald-400">
          Completed ({totalEmails} email{totalEmails !== 1 ? 's' : ''})
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-slate-200">
        {emailsSent} of {totalEmails} sent
      </span>
      {emailsSent > 0 && (
        <div className="h-2 w-16 overflow-hidden rounded-full bg-slate-700">
          <div
            className="h-full bg-cyan-500 transition-all duration-300"
            style={{ width: `${(emailsSent / totalEmails) * 100}%` }}
          />
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status, sendingStatus }: { status: string; sendingStatus?: string }) {
  const statusColors = {
    draft: 'bg-slate-500/15 text-slate-400 border border-slate-500/20',
    active: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
    paused: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
    completed: 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20',
    failed: 'bg-rose-500/15 text-rose-400 border border-rose-500/20',
  }

  return (
    <div className="flex gap-2">
      <span
        className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${statusColors[status.toLowerCase() as keyof typeof statusColors] || statusColors.draft}`}
      >
        {status}
      </span>
      {sendingStatus && sendingStatus !== 'idle' && (
        <span className="inline-flex rounded-full bg-slate-700 px-3 py-1 text-sm font-semibold text-slate-300 border border-slate-600">
          {sendingStatus}
        </span>
      )}
    </div>
  )
}

interface StatCardProps {
  icon: any
  label: string
  value: string
  subtitle?: string
  color: 'blue' | 'green' | 'purple' | 'orange'
}

function StatCard({ icon: Icon, label, value, subtitle, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-cyan-500/15 text-cyan-400',
    green: 'bg-emerald-500/15 text-emerald-400',
    purple: 'bg-indigo-500/15 text-indigo-400',
    orange: 'bg-amber-500/15 text-amber-400',
  }

  return (
    <div className="stat-card p-6">
      <div className={`inline-flex rounded-xl p-3 ${colorClasses[color]}`}>
        <Icon size={24} />
      </div>
      <h3 className="mt-4 text-sm font-medium text-slate-400">{label}</h3>
      <p className="mt-1 text-3xl font-bold text-slate-100">{value}</p>
      {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
    </div>
  )
}

interface MetricCardProps {
  label: string
  value: string
  color: 'green' | 'blue' | 'purple'
}

function MetricCard({ label, value, color }: MetricCardProps) {
  const colorClasses = {
    green: 'text-emerald-400',
    blue: 'text-cyan-400',
    purple: 'text-indigo-400',
  }

  return (
    <div className="text-center">
      <p className="text-sm font-medium text-slate-400">{label}</p>
      <p className={`mt-2 text-4xl font-bold ${colorClasses[color]}`}>{value}</p>
    </div>
  )
}
